import React from 'react';
import { Skeleton } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Area } from '../../types/tables';
import { useAppSelector } from '../../hooks/redux';
import { isAdmin, isManager } from '../../utils/roleHelpers';
import { AreaOccupancy } from './tableHelpers';

interface AreaSelectorProps {
  areas: Area[];
  selectedAreaId: string | null;
  onAreaChange: (areaId: string | null) => void;
  onAddArea?: () => void;
  loading?: boolean;
  /** Per-area "occupied/total" shown inside each pill — the selector doubles as information. */
  occupancyByArea?: Record<string, AreaOccupancy>;
  totalTables?: number;
}

const AreaSelector: React.FC<AreaSelectorProps> = ({
  areas,
  selectedAreaId,
  onAreaChange,
  onAddArea,
  loading = false,
  occupancyByArea,
  totalTables,
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const hasAdminAccess = isAdmin(user?.role) || isManager(user?.role);

  if (loading) {
    return (
      <div className="area-pills-container">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton.Button
            key={index}
            active
            style={{ width: 100, height: 40, borderRadius: 20 }}
          />
        ))}
      </div>
    );
  }

  const renderCount = (areaId: string) => {
    const occ = occupancyByArea?.[areaId];
    if (!occ || occ.total === 0) return null;
    // "hot" when the area is nearly full — draws the hostess's eye
    const hot = occ.occupied / occ.total >= 0.75;
    return (
      <span className={`area-pill-count${hot ? ' hot' : ''}`}>
        {occ.occupied}/{occ.total}
      </span>
    );
  };

  return (
    <div className="area-pills-container">
      <button
        className={`area-pill ${selectedAreaId === null ? 'selected' : ''}`}
        onClick={() => onAreaChange(null)}
      >
        Todas
        {totalTables ? <span className="area-pill-count">{totalTables}</span> : null}
      </button>
      {areas.map((area) => (
        <button
          key={area.id}
          className={`area-pill ${selectedAreaId === area.id ? 'selected' : ''}`}
          onClick={() => onAreaChange(area.id)}
        >
          {area.name}
          {renderCount(area.id)}
        </button>
      ))}

      {onAddArea && hasAdminAccess && (
        <button className="area-pill-add" onClick={onAddArea}>
          <PlusOutlined />
          Nueva Área
        </button>
      )}
    </div>
  );
};

export default AreaSelector;
